<template>
  <div class="w-full">
    <!-- Centered Loading State -->
    <AppLoader
      v-if="loading"
      :isVisible="loading"
      variant="fullscreen"
      message="Loading Leave Requests..."
      subtext="Fetching faculty and student leave applications"
      color="indigo"
      icon="sparkle"
    />

    <!-- Error State -->
    <div
      v-else-if="error"
      class="bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800/50 rounded-xl p-4 mb-4 sm:mb-6"
    >
      <div class="flex items-center">
        <XCircle class="w-5 h-5 text-rose-500 mr-2 shrink-0" />
        <p class="text-rose-700 dark:text-rose-300 font-medium text-sm">{{ error }}</p>
      </div>
      <button
        @click="fetchLeaveRequests"
        class="mt-2 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline"
      >
        Try Again
      </button>
    </div>

    <!-- Filters Card -->
    <div
      class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl sm:rounded-2xl p-3.5 sm:p-4 mb-4 sm:mb-6 shadow-xs"
    >
      <!-- Header with Title + Toggle Button (mobile only) -->
      <div class="flex items-center justify-between gap-3 mb-3">
        <div class="flex items-center gap-2">
          <div
            class="w-8 h-8 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-lg flex items-center justify-center shrink-0"
          >
            <SlidersHorizontal class="w-4 h-4" />
          </div>
          <div>
            <h2 class="text-xs sm:text-sm font-bold text-slate-800 dark:text-slate-100">
              Filter Leave Requests
            </h2>
            <p class="text-[11px] text-slate-400 dark:text-slate-500">
              Search and narrow down requests by type, month, or year
            </p>
          </div>
        </div>

        <!-- Toggle button for mobile -->
        <button
          type="button"
          @click="showMobileFilters = !showMobileFilters"
          class="w-8 h-8 md:hidden inline-flex items-center justify-center rounded-xl border border-slate-200 dark:border-[#263449] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#18243A] transition-colors shrink-0"
          :class="{ 'bg-slate-100 dark:bg-[#18243A]': showMobileFilters }"
          aria-label="Toggle mobile filters"
        >
          <Filter class="w-4 h-4 shrink-0" />
        </button>
      </div>

      <!-- Filter Inputs -->
      <div
        :class="{
          'hidden md:grid': !showMobileFilters,
          'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3': true,
        }"
        class="transition-all duration-300"
      >
        <!-- Search -->
        <div class="space-y-1">
          <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Search</label>
          <div class="relative">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
            />
            <input
              v-model="filters.search"
              type="text"
              placeholder="Search by name or reason..."
              class="w-full pl-9 pr-3.5 py-2.5 min-h-[42px] text-xs sm:text-sm font-semibold bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 placeholder-slate-400 border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all"
              @input="debouncedSearch"
            />
          </div>
        </div>

        <!-- Leave Type -->
        <div class="space-y-1">
          <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Leave Type</label>
          <select
            v-model="filters.leaveType"
            class="w-full px-3 py-2.5 min-h-[42px] text-xs sm:text-sm font-semibold bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all cursor-pointer"
            @change="fetchLeaveRequests"
          >
            <option value="">All Types</option>
            <option value="Casual">Casual</option>
            <option value="Sick">Sick</option>
            <option value="Personal">Personal</option>
            <option value="Maternity">Maternity</option>
            <option value="Paternity">Paternity</option>
            <option value="Unpaid">Unpaid</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <!-- Month -->
        <div class="space-y-1">
          <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Month</label>
          <select
            v-model="filters.month"
            class="w-full px-3 py-2.5 min-h-[42px] text-xs sm:text-sm font-semibold bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all cursor-pointer"
            @change="fetchLeaveRequests"
          >
            <option value="">All Months</option>
            <option
              v-for="(month, index) in months"
              :key="index"
              :value="index + 1"
            >
              {{ month }}
            </option>
          </select>
        </div>

        <!-- Year -->
        <div class="space-y-1">
          <label class="text-[11px] font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Year</label>
          <select
            v-model="filters.year"
            class="w-full px-3 py-2.5 min-h-[42px] text-xs sm:text-sm font-semibold bg-slate-50/80 dark:bg-[#18243A] text-slate-900 dark:text-slate-100 border border-slate-200/80 dark:border-[#263449] rounded-xl focus:bg-white dark:focus:bg-[#111C30] focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all cursor-pointer"
            @change="fetchLeaveRequests"
          >
            <option value="">All Years</option>
            <option
              v-for="year in years"
              :key="year"
              :value="year"
            >
              {{ year }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Stats Cards (Matching Student Stats Card design system) -->
    <div class="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3 lg:gap-4 mb-4 sm:mb-6">
      <!-- Total Requests -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
      >
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <div class="p-1.5 bg-blue-50 dark:bg-blue-950/40 rounded-lg text-blue-600 dark:text-blue-400">
              <FileText class="w-4 h-4" />
            </div>
            <span
              class="text-[10px] font-semibold bg-slate-100 dark:bg-[#18243A] text-slate-500 dark:text-slate-400 px-1.5 py-0.5 rounded"
            >
              Total
            </span>
          </div>
          <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
            Total Applications
          </h3>
          <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
            {{ totalRequests }}
          </p>
        </div>
        <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1.5">
          All submitted applications
        </p>
      </div>

      <!-- Pending Review Card -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
      >
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <div class="p-1.5 bg-amber-50 dark:bg-amber-950/40 rounded-lg text-amber-600 dark:text-amber-400">
              <Clock class="w-4 h-4" />
            </div>
            <span
              class="text-[10px] font-semibold bg-amber-50 dark:bg-amber-950/40 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded"
            >
              Pending
            </span>
          </div>
          <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
            Pending Review
          </h3>
          <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
            {{ leaveSummary.totalPending }}
          </p>
        </div>
        <div>
          <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-1.5">
            <div
              class="bg-amber-500 h-full rounded-full transition-all duration-500"
              :style="{ width: `${pendingPct}%` }"
            ></div>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1">
            {{ pendingPct }}% awaiting decision
          </p>
        </div>
      </div>

      <!-- Approved Card -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
      >
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <div class="p-1.5 bg-emerald-50 dark:bg-emerald-950/40 rounded-lg text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 class="w-4 h-4" />
            </div>
            <span
              class="text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded"
            >
              Approved
            </span>
          </div>
          <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
            Approved Requests
          </h3>
          <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
            {{ leaveSummary.totalApproved }}
          </p>
        </div>
        <div>
          <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-1.5">
            <div
              class="bg-emerald-500 h-full rounded-full transition-all duration-500"
              :style="{ width: `${approvedPct}%` }"
            ></div>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1">
            {{ approvedPct }}% approval rate
          </p>
        </div>
      </div>

      <!-- Declined Card -->
      <div
        class="bg-white dark:bg-[#111C30] border border-slate-200/90 dark:border-[#263449] rounded-xl p-3 sm:p-3.5 relative shadow-xs flex flex-col justify-between hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200"
      >
        <div>
          <div class="flex items-center justify-between mb-1.5">
            <div class="p-1.5 bg-rose-50 dark:bg-rose-950/40 rounded-lg text-rose-600 dark:text-rose-400">
              <XCircle class="w-4 h-4" />
            </div>
            <span
              class="text-[10px] font-semibold bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400 px-1.5 py-0.5 rounded"
            >
              Declined
            </span>
          </div>
          <h3 class="text-xs font-semibold text-slate-600 dark:text-slate-300">
            Declined Requests
          </h3>
          <p class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white mt-0.5 tracking-tight">
            {{ leaveSummary.totalDeclined }}
          </p>
        </div>
        <div>
          <div class="w-full bg-slate-100 dark:bg-[#1E293B] h-1.5 rounded-full overflow-hidden mt-1.5">
            <div
              class="bg-rose-500 h-full rounded-full transition-all duration-500"
              :style="{ width: `${declinedPct}%` }"
            ></div>
          </div>
          <p class="text-slate-400 dark:text-slate-500 text-[11px] mt-1">
            {{ declinedPct }}% rejected applications
          </p>
        </div>
      </div>
    </div>

    <!-- Premium Leave Request Cards Grid -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
      <div
        v-for="request in sortedLeaveRequests"
        :key="request._id"
        class="bg-white dark:bg-[#111C30] border rounded-2xl p-4 shadow-xs hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700 transition-all duration-200 flex flex-col justify-between relative overflow-hidden group"
        :class="[
          request.status === 'Pending'
            ? 'border-amber-300/80 dark:border-amber-600/50'
            : request.status === 'Approved'
            ? 'border-emerald-200/80 dark:border-emerald-700/50'
            : 'border-slate-200/90 dark:border-[#263449]',
        ]"
      >
        <div>
          <!-- Header: Requester Profile & Status Pill -->
          <div class="flex items-start justify-between gap-2 mb-3">
            <!-- Avatar + Name -->
            <div class="flex items-center space-x-3 min-w-0">
              <div class="w-10 h-10 sm:w-11 sm:h-11 min-w-[40px] max-w-[40px] min-h-[40px] max-h-[40px] sm:min-w-[44px] sm:max-w-[44px] sm:min-h-[44px] sm:max-h-[44px] rounded-xl overflow-hidden shrink-0 border border-slate-200/90 dark:border-slate-700 flex items-center justify-center bg-slate-100 dark:bg-[#18243A] shadow-xs">
                <img
                  v-if="request.user_id?.photo"
                  :src="request.user_id.photo"
                  :alt="request.user_id?.name"
                  class="w-full h-full object-cover"
                />
                <div
                  v-else
                  class="w-full h-full bg-gradient-to-br from-indigo-500 to-blue-600 text-white flex items-center justify-center font-bold text-xs"
                >
                  {{ getInitials(request.user_id?.name) }}
                </div>
              </div>

              <div class="min-w-0">
                <h3 class="text-xs sm:text-sm font-black text-slate-900 dark:text-white truncate">
                  {{ request.user_id?.name || "Unknown Requester" }}
                </h3>
                <div class="flex items-center text-[11px] text-slate-500 dark:text-slate-400 gap-1.5 truncate mt-0.5">
                  <span class="font-bold text-indigo-600 dark:text-indigo-400 capitalize">{{ request.user_id?.role || "Staff" }}</span>
                  <span class="text-slate-300 dark:text-slate-600">•</span>
                  <span class="truncate">{{ request.user_id?.email || "No email" }}</span>
                </div>
              </div>
            </div>

            <!-- Status Badge & 3-dot Menu -->
            <div class="flex items-center gap-1.5 shrink-0">
              <!-- Status Badge with glowing pulse dot for pending -->
              <span
                :class="[
                  'inline-flex items-center gap-1.5 px-2.5 py-0.5 text-[11px] font-bold rounded-full border shadow-xs whitespace-nowrap',
                  getStatusBadgeStyle(request.status),
                ]"
              >
                <span
                  v-if="request.status === 'Pending'"
                  class="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"
                ></span>
                <span
                  v-else-if="request.status === 'Approved'"
                  class="w-1.5 h-1.5 rounded-full bg-emerald-500"
                ></span>
                <span
                  v-else
                  class="w-1.5 h-1.5 rounded-full bg-rose-500"
                ></span>
                {{ request.status }}
              </span>

              <!-- 3-dot Menu (Undo) -->
              <div
                v-if="request.status !== 'Pending'"
                class="relative menu-container"
              >
                <button
                  @click.stop="toggleMenu(request._id)"
                  class="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-[#18243A] transition-colors"
                >
                  <EllipsisVertical class="w-4 h-4" />
                </button>

                <div
                  v-if="openMenu === request._id"
                  class="absolute right-0 mt-1 w-36 bg-white dark:bg-[#18243A] border border-slate-200 dark:border-[#263449] rounded-xl shadow-lg z-50 p-1"
                >
                  <button
                    @click="undoStatus(request._id)"
                    :disabled="updatingStatus === request._id"
                    class="w-full text-left px-3 py-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg disabled:opacity-50 transition-colors"
                  >
                    Undo Decision
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Leave Schedule Card Ribbon -->
          <div class="bg-slate-50/90 dark:bg-[#152238] rounded-xl p-3 border border-slate-200/60 dark:border-[#202E44] space-y-2 mb-3">
            <div class="flex items-center justify-between">
              <span class="inline-flex items-center gap-1.5 text-xs font-bold text-slate-800 dark:text-slate-200">
                <span class="w-2 h-2 rounded-full bg-indigo-500"></span>
                {{ request.leave_type }} Leave
              </span>
              <span class="text-[11px] font-black bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-400 px-2 py-0.5 rounded-md">
                {{ request.duration_days }} {{ request.duration_days > 1 ? 'Days' : 'Day' }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 font-medium">
              <Calendar class="w-3.5 h-3.5 text-indigo-500 dark:text-indigo-400 shrink-0" />
              <span class="truncate">{{ formatDateRange(request.from_datetime, request.to_datetime) }}</span>
            </div>
          </div>

          <!-- Reason Section with Elegant Quote Line -->
          <div class="relative pl-3 border-l-2 border-indigo-200 dark:border-indigo-800/60 mb-3">
            <p class="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Reason
            </p>
            <p class="text-xs text-slate-700 dark:text-slate-200 italic line-clamp-2 mt-0.5" :title="request.reason">
              "{{ request.reason || 'No reason specified' }}"
            </p>
          </div>
        </div>

        <!-- Bottom Actions / Processing Info -->
        <div class="pt-2.5 border-t border-slate-100 dark:border-[#1E293B] mt-auto">
          <template v-if="request.status === 'Pending'">
            <div class="grid grid-cols-2 gap-2">
              <!-- Approve Button -->
              <button
                @click="updateStatus(request._id, 'Approved')"
                :disabled="updatingStatus === request._id"
                class="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-1.5 px-3 rounded-xl text-xs transition-all flex items-center justify-center gap-1 shadow-xs hover:shadow-emerald-500/20"
              >
                <span
                  v-if="updatingStatus === request._id"
                  class="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"
                ></span>
                <Check class="w-3.5 h-3.5" v-else />
                Approve
              </button>

              <!-- Decline Button -->
              <button
                @click="updateStatus(request._id, 'Declined')"
                :disabled="updatingStatus === request._id"
                class="bg-slate-100 dark:bg-[#18243A] hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-700 dark:text-slate-300 hover:text-rose-600 dark:hover:text-rose-400 border border-slate-200/80 dark:border-[#263449] hover:border-rose-200 font-bold py-1.5 px-3 rounded-xl text-xs transition-all flex items-center justify-center gap-1 shadow-xs"
              >
                <span
                  v-if="updatingStatus === request._id"
                  class="w-3 h-3 border-2 border-rose-600/30 border-t-rose-600 rounded-full animate-spin"
                ></span>
                <X class="w-3.5 h-3.5" v-else />
                Decline
              </button>
            </div>
          </template>

          <template v-else>
            <div class="flex items-center justify-between text-[11px] text-slate-400 dark:text-slate-500 py-0.5">
              <span>Processed on:</span>
              <span class="font-bold text-slate-700 dark:text-slate-300">
                {{ formatDate(request.processed_on) }}
              </span>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="pagination.totalPages > 1"
      class="flex justify-center items-center space-x-2 mt-6 sm:mt-8"
    >
      <button
        @click="changePage(pagination.page - 1)"
        :disabled="pagination.page === 1 || loading"
        class="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
      >
        Previous
      </button>

      <span class="px-3 py-1.5 text-xs font-semibold text-slate-600 dark:text-slate-400">
        Page {{ pagination.page }} of {{ pagination.totalPages }}
      </span>

      <button
        @click="changePage(pagination.page + 1)"
        :disabled="pagination.page === pagination.totalPages || loading"
        class="px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-300 bg-white dark:bg-[#111C30] border border-slate-200 dark:border-[#263449] rounded-lg hover:bg-slate-50 dark:hover:bg-[#18243A] disabled:opacity-50 disabled:cursor-not-allowed shadow-xs"
      >
        Next
      </button>
    </div>

    <!-- Empty State -->
    <div
      v-if="!loading && leaveRequests.length === 0"
      class="text-center py-16 sm:py-20 rounded-2xl border-2 border-dashed border-slate-200 dark:border-[#263449] bg-white/40 dark:bg-[#111C30]/40 my-4"
    >
      <div
        class="w-14 h-14 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-3"
      >
        <FileText class="w-7 h-7" />
      </div>
      <h3 class="text-base sm:text-lg font-bold text-slate-900 dark:text-white mb-1">
        No leave requests found
      </h3>
      <p class="text-xs sm:text-sm text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
        No leave requests match the selected search and filter criteria.
      </p>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted, watch, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Search,
    Filter,
    Calendar,
    Clock,
    CheckCircle2,
    XCircle,
    Check,
    X,
    FileText,
    SlidersHorizontal,
    EllipsisVertical,
  } from "lucide-vue-next";
  import { useGlobalToast } from "@/utils/useGlobalToast";
  const { showToast } = useGlobalToast();

  const store = useStore();

  // Reactive data
  const loading = ref(false);
  const error = ref(null);
  const updatingStatus = ref(null);
  const searchTimeout = ref(null);
  const showMobileFilters = ref(false);
  const openMenu = ref(null);

  const toggleMenu = (id) => {
    openMenu.value = openMenu.value === id ? null : id;
  };
  // Close menu when clicking outside
  const handleClickOutside = (event) => {
    if (!event.target.closest(".menu-container")) {
      openMenu.value = null;
    }
  };

  onMounted(() => {
    document.addEventListener("click", handleClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClickOutside);
  });

  const filters = ref({
    search: "",
    leaveType: "",
    month: "",
    year: new Date().getFullYear().toString(),
  });

  const pagination = ref({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // Static data
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const years = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  });

  // Computed properties
  const leaveRequests = computed(() => store.getters.getLeaveRequests || []);
  const leaveSummary = computed(
    () =>
      store.getters.getLeaveSummary || {
        totalPending: 0,
        totalApproved: 0,
        totalDeclined: 0,
      }
  );

  const totalRequests = computed(() => {
    return (
      (leaveSummary.value.totalPending || 0) +
      (leaveSummary.value.totalApproved || 0) +
      (leaveSummary.value.totalDeclined || 0)
    );
  });

  const pendingPct = computed(() =>
    totalRequests.value
      ? Math.round((leaveSummary.value.totalPending / totalRequests.value) * 100)
      : 0
  );

  const approvedPct = computed(() =>
    totalRequests.value
      ? Math.round((leaveSummary.value.totalApproved / totalRequests.value) * 100)
      : 0
  );

  const declinedPct = computed(() =>
    totalRequests.value
      ? Math.round((leaveSummary.value.totalDeclined / totalRequests.value) * 100)
      : 0
  );

  // Sort leave requests to show pending ones first
  const sortedLeaveRequests = computed(() => {
    const requests = [...leaveRequests.value];
    return requests.sort((a, b) => {
      if (a.status === "Pending" && b.status !== "Pending") return -1;
      if (a.status !== "Pending" && b.status === "Pending") return 1;

      return (
        new Date(b.created_at || b.createdAt) -
        new Date(a.created_at || a.createdAt)
      );
    });
  });

  // Methods
  const fetchLeaveRequests = async (resetPage = true) => {
    if (resetPage) {
      pagination.value.page = 1;
    }

    loading.value = true;
    error.value = null;

    try {
      const result = await store.dispatch("fetchMyLeaves", {
        page: pagination.value.page,
        limit: pagination.value.limit,
        search: filters.value.search,
        leave_type: filters.value.leaveType,
        month: filters.value.month,
        year: filters.value.year,
      });

      if (result && result.pagination) {
        pagination.value = result.pagination;
      }
    } catch (err) {
      error.value = "Failed to fetch leave requests. Please try again.";
      showToast("error", "Failed to fetch leave requests. Please try again.");
      console.error("Error fetching leave requests:", err);
    } finally {
      loading.value = false;
    }
  };

  const debouncedSearch = () => {
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value);
    }

    searchTimeout.value = setTimeout(() => {
      fetchLeaveRequests(true);
    }, 500);
  };

  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.value.totalPages) {
      pagination.value.page = newPage;
      fetchLeaveRequests(false);
    }
  };

  const updateStatus = async (leaveId, status) => {
    updatingStatus.value = leaveId;

    try {
      await store.dispatch("updateLeaveStatus", { leaveId, status });
      await fetchLeaveRequests(false);
      showToast(
        "success",
        `Leave request ${status.toLowerCase()} successfully!`,
      );
    } catch (err) {
      error.value = `Failed to ${status.toLowerCase()} leave request. Please try again.`;
      showToast(
        "error",
        `Failed to ${status.toLowerCase()} leave request. Please try again.`,
      );
      console.error("Error updating leave status:", err);
    } finally {
      updatingStatus.value = null;
    }
  };

  const undoStatus = async (leaveId) => {
    updatingStatus.value = leaveId;

    try {
      await store.dispatch("updateLeaveStatus", { leaveId, status: "Pending" });
      await fetchLeaveRequests(false);
      showToast("success", "Leave request reverted to Pending successfully!");
    } catch (err) {
      error.value = "Failed to undo decision. Please try again.";
      showToast("error", "Failed to undo decision. Please try again.");
      console.error("Error undoing leave status:", err);
    } finally {
      updatingStatus.value = null;
    }
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case "Pending":
        return "bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-400 border-amber-200/70 dark:border-amber-800/40";
      case "Approved":
        return "bg-emerald-50 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 border-emerald-200/70 dark:border-emerald-800/40";
      case "Declined":
        return "bg-rose-50 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400 border-rose-200/70 dark:border-rose-800/40";
      default:
        return "bg-slate-100 dark:bg-[#18243A] text-slate-700 dark:text-slate-300 border-slate-200 dark:border-[#263449]";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateRange = (startDate, endDate) => {
    const start = formatDate(startDate);
    const end = formatDate(endDate);
    return startDate === endDate ? start : `${start} - ${end}`;
  };

  onMounted(() => {
    fetchLeaveRequests();
  });

  watch(
    [
      () => filters.value.leaveType,
      () => filters.value.month,
      () => filters.value.year,
    ],
    () => {
      fetchLeaveRequests(true);
    },
  );

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .trim()
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };
</script>
